import { Industry, CountryCode } from '../types/types';

// Company persona definition
export interface CompanyPersona {
  companyName: string;
  websiteUrl: string;
  industry: Industry;
  preferredCountryCodes?: CountryCode[];
}

// Company personas aligned with specific industries
export const companyPersonas: CompanyPersona[] = [
  {
    companyName: 'US Government Services',
    websiteUrl: 'www.usgovernmentservices.example.gov',
    industry: 'Government',
    preferredCountryCodes: ['US'],
  },
  {
    companyName: 'Federal Reserve System',
    websiteUrl: 'www.fedsecure.example.gov',
    industry: 'Government',
    preferredCountryCodes: ['US'],
  },
  {
    companyName: 'Department of Defense Technology',
    websiteUrl: 'www.defense-tech.example.gov',
    industry: 'Government',
    preferredCountryCodes: ['US'],
  },
  {
    companyName: 'Logistic Systems International',
    websiteUrl: 'www.logisticsystems.example.com',
    industry: 'Transportation and Logistics',
  },
  {
    companyName: 'Global Freight Solutions',
    websiteUrl: 'www.globalfreight.example.com',
    industry: 'Transportation and Logistics',
  },
  {
    companyName: 'AeroLogistics Partners',
    websiteUrl: 'www.aerologistics.example.com',
    industry: 'Transportation and Logistics',
  },
  {
    companyName: 'CloudEdge Technologies',
    websiteUrl: 'www.cloudedge-tech.example.net',
    industry: 'Software and Internet',
  },
  {
    companyName: 'Cyber Shield Security',
    websiteUrl: 'www.cyber-shield-enterprise.example.io',
    industry: 'Software and Internet',
  },
  {
    companyName: 'DevOps Innovations',
    websiteUrl: 'www.devops-innovations.example.com',
    industry: 'Software and Internet',
  },
  {
    companyName: 'DataFlow Systems',
    websiteUrl: 'www.dataflow.example.com',
    industry: 'Software and Internet',
  },
  {
    companyName: 'Innovate Healthcare Inc',
    websiteUrl: 'www.healthcareinnovations.example.com',
    industry: 'Healthcare',
  },
  {
    companyName: 'MedTech Solutions',
    websiteUrl: 'www.medtech-solutions.example.com',
    industry: 'Healthcare',
  },
  {
    companyName: 'Global Health Systems',
    websiteUrl: 'www.globalhealthsys.example.com',
    industry: 'Healthcare',
  },
  {
    companyName: 'Digital Frontier Communications',
    websiteUrl: 'www.digitalfrontiertelecom.example.com',
    industry: 'Telecommunications',
  },
  {
    companyName: 'Global Network Solutions',
    websiteUrl: 'www.globalnetwork.example.com',
    industry: 'Telecommunications',
  },
  {
    companyName: 'TeleConnect Systems',
    websiteUrl: 'www.teleconnect.example.com',
    industry: 'Telecommunications',
  },
  {
    companyName: 'Financial Innovation Group',
    websiteUrl: 'www.financialinnovation.example.com',
    industry: 'Financial Services',
  },
  {
    companyName: 'Global Investment Partners',
    websiteUrl: 'www.globalinvestment.example.com',
    industry: 'Financial Services',
  },
  {
    companyName: 'Wealth Management Solutions',
    websiteUrl: 'www.wealthmanage.example.com',
    industry: 'Financial Services',
  },
  {
    companyName: 'NextGen Cloud Services',
    websiteUrl: 'www.nextgen-cloud.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'Quantum AI Computing Group',
    websiteUrl: 'www.quantum-ai.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'Silicon Valley Electronics',
    websiteUrl: 'www.svelectronics.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'Digital Hardware Solutions',
    websiteUrl: 'www.digitalhw.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'EcoTech Energy Solutions',
    websiteUrl: 'www.ecotechgreenenergy.example.com',
    industry: 'Energy - Power and Utilities',
  },
  {
    companyName: 'Renewable Power Systems',
    websiteUrl: 'www.renewablepower.example.com',
    industry: 'Energy - Power and Utilities',
  },
  {
    companyName: 'Smart Grid Technologies',
    websiteUrl: 'www.smartgrid.example.com',
    industry: 'Energy - Power and Utilities',
  },
  {
    companyName: 'Manufacturing Excellence Corp',
    websiteUrl: 'www.mfgexcellence.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'Industrial Automation Systems',
    websiteUrl: 'www.indauto.example.com',
    industry: 'Computers and Electronics',
  },
  {
    companyName: 'Enterprise Commerce Solutions',
    websiteUrl: 'www.enterprisecommerce.example.com',
    industry: 'Software and Internet',
  },
  {
    companyName: 'Digital Marketplace Systems',
    websiteUrl: 'www.digitalmarket.example.com',
    industry: 'Software and Internet',
  },
];

// Track recently used companies to avoid repetition
let recentlyUsedCompanies: Set<string> = new Set();
const MEMORY_SIZE = 5; // Remember last 5 companies

export const getRandomCompanyPersona = (): CompanyPersona => {
  // Filter out recently used companies
  const availableCompanies = companyPersonas.filter(
    (company) => !recentlyUsedCompanies.has(company.companyName),
  );

  // Reset memory if too many companies are filtered out
  if (availableCompanies.length < 3) {
    recentlyUsedCompanies.clear();
  }

  // Get count of companies per industry
  const industryCounts = availableCompanies.reduce((acc, persona) => {
    acc[persona.industry] = (acc[persona.industry] || 0) + 1;
    return acc;
  }, {} as Record<Industry, number>);

  // Calculate weights (inverse of frequency) to favor less represented industries
  const weights = availableCompanies.map((persona) => {
    const count = industryCounts[persona.industry];
    return 1 / count; // Lower frequency = higher weight
  });

  // Calculate sum of weights for normalization
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

  // Normalize weights to sum to 1
  const normalizedWeights = weights.map((w) => w / totalWeight);

  // Random number between 0 and 1
  let random = Math.random();

  // Find the index based on cumulative probability
  let cumulativeWeight = 0;
  let selectedIndex = 0;
  for (let i = 0; i < normalizedWeights.length; i++) {
    cumulativeWeight += normalizedWeights[i];
    if (random <= cumulativeWeight) {
      selectedIndex = i;
      break;
    }
  }

  // Update recently used companies
  const selected = availableCompanies[selectedIndex];
  recentlyUsedCompanies.add(selected.companyName);
  if (recentlyUsedCompanies.size > MEMORY_SIZE) {
    recentlyUsedCompanies.delete(Array.from(recentlyUsedCompanies)[0]);
  }

  return selected;
};
